import { useId, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, ChevronsUpDown, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@rudi/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@rudi/ui/popover';
import { cn } from '@rudi/ui/lib/utils';
import { PageHeader } from '../components/page';
import {
  chatModelError,
  isChatModelConflict,
  useChatModel,
  useChatModelList,
  useSaveChatModel,
  type ChatModelChoice,
  type ChatModelSettings,
  type OpenRouterModel,
} from '../api/chat-model';

const CONFLICT = 'Someone else changed the model. The page shows the latest values.';
const TEXT_ONLY =
  'This model cannot read images or PDFs. Turns with attachments use the attachment model.';

export function formatContext(tokens: number | null): string {
  if (tokens === null) return 'n/a';
  if (tokens >= 1_000_000) return `${Number((tokens / 1_000_000).toFixed(1))}M`;
  if (tokens >= 1_000) return `${Math.round(tokens / 1_000)}K`;
  return String(tokens);
}

export function formatPrice(price: number | null): string {
  if (price === null) return 'n/a';
  if (price === 0) return 'free';
  return `$${price.toFixed(price < 0.1 ? 3 : 2)}`;
}

const readsBoth = (model: OpenRouterModel | undefined) =>
  !!model?.supports_images && !!model.supports_pdfs;

function InputBadges({ model }: { model: OpenRouterModel }) {
  const inputs = [
    model.input_modalities.includes('text') && 'Text',
    model.supports_images && 'Images',
    model.supports_pdfs && 'PDFs',
  ].filter((input): input is string => !!input);
  return (
    <span className="flex flex-wrap gap-1">
      {inputs.map(input => (
        <Badge key={input} variant="secondary" className="font-normal">
          {input}
        </Badge>
      ))}
    </span>
  );
}

function ModelDetails({ model }: { model: OpenRouterModel }) {
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-1 text-left">
      <span className="font-medium">{model.name || model.id}</span>
      <span className="break-all font-mono text-xs text-muted-foreground">{model.id}</span>
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>{formatContext(model.context_length)} context</span>
        <span>
          {formatPrice(model.prompt_price_per_million)} in /{' '}
          {formatPrice(model.completion_price_per_million)} out per 1M tokens
        </span>
        <InputBadges model={model} />
      </span>
    </span>
  );
}

function ModelCombobox({
  label,
  hint,
  value,
  models,
  byId,
  onChange,
  clearLabel,
  required,
  disabled,
  notice,
}: {
  label: string;
  hint?: ReactNode;
  value: string | null;
  models: OpenRouterModel[];
  byId: Map<string, OpenRouterModel>;
  onChange: (id: string | null) => void;
  /** Offer an entry that clears the field. */
  clearLabel?: string;
  required?: boolean;
  disabled?: boolean;
  notice?: string;
}) {
  const [open, setOpen] = useState(false);
  const hintId = useId();
  const selected = value ? byId.get(value) : undefined;
  return (
    <div className="space-y-1 text-sm">
      <div className="flex items-baseline justify-between gap-2">
        <span id={`${hintId}-label`}>{label}</span>
        <span className="text-xs text-muted-foreground">{required ? 'Required' : 'Optional'}</span>
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={label}
            aria-describedby={hint ? hintId : undefined}
            disabled={disabled}
            className="h-auto min-h-9 w-full justify-between whitespace-normal py-2 text-left font-normal"
          >
            {selected ? (
              <ModelDetails model={selected} />
            ) : value ? (
              <span className="flex flex-col">
                <span className="font-mono text-xs">{value}</span>
                <span className="text-xs text-destructive">Not in Chat's model list</span>
              </span>
            ) : (
              <span className="text-muted-foreground">{clearLabel ?? 'Choose a model'}</span>
            )}
            <ChevronsUpDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(42rem,90vw)] p-0">
          <Command>
            <CommandInput placeholder="Search by name or id" aria-label={`Search ${label}`} />
            {notice && <p className="border-b px-3 py-2 text-xs text-muted-foreground">{notice}</p>}
            <CommandList className="max-h-[360px]">
              <CommandEmpty>No model matches.</CommandEmpty>
              <CommandGroup>
                {clearLabel && (
                  <CommandItem
                    value="__none__"
                    keywords={[clearLabel]}
                    onSelect={() => {
                      onChange(null);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn('mr-2 h-4 w-4', value ? 'opacity-0' : 'opacity-100')} />
                    {clearLabel}
                  </CommandItem>
                )}
                {models.map(model => (
                  <CommandItem
                    key={model.id}
                    value={model.id}
                    keywords={[model.name]}
                    onSelect={() => {
                      onChange(model.id);
                      setOpen(false);
                    }}
                    className="items-start"
                  >
                    <Check
                      className={cn(
                        'mr-2 mt-0.5 h-4 w-4 shrink-0',
                        value === model.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <ModelDetails model={model} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {hint && (
        <div id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </div>
      )}
    </div>
  );
}

function sameChoice(a: ChatModelChoice, b: ChatModelChoice) {
  return (
    a.model === b.model &&
    a.attachment_model === b.attachment_model &&
    a.fallback_model === b.fallback_model
  );
}

function Summary({ settings }: { settings: ChatModelSettings }) {
  const saved = settings.model !== null;
  const rows: [string, ReactNode][] = [
    ['Environment', settings.environment ?? 'Not reported by Chat'],
    ['Model in use', <span className="font-mono">{settings.effective.model}</span>],
    [
      'Source',
      saved
        ? 'Saved in Cortex'
        : `Environment default (${settings.default_model}), nobody has saved a choice yet`,
    ],
    [
      'Attachment model',
      settings.effective.attachment_model ? (
        <span className="font-mono">{settings.effective.attachment_model}</span>
      ) : (
        'None'
      ),
    ],
    [
      'Fallback model',
      settings.effective.fallback_model ? (
        <span className="font-mono">{settings.effective.fallback_model}</span>
      ) : (
        'None'
      ),
    ],
    [
      'Last changed',
      settings.updated_at
        ? `${new Date(settings.updated_at).toLocaleString()} by ${settings.updated_by ?? 'unknown'}`
        : 'Never',
    ],
  ];
  return (
    <section aria-label="Current patient chat model" className="rounded-3xl border p-4">
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
        {rows.map(([term, detail]) => (
          <div key={term} className="contents">
            <dt className="text-muted-foreground">{term}</dt>
            <dd className="break-all">{detail}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function ChatModelForm({
  settings,
  models,
}: {
  settings: ChatModelSettings;
  models: OpenRouterModel[];
}) {
  const mutation = useSaveChatModel();
  // The draft keeps the revision it started from, so a save over someone
  // else's change gets Chat's 409 instead of overwriting it.
  const [draft, setDraft] = useState<(ChatModelChoice & { revision: number }) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const base = settings.effective;
  const current: ChatModelChoice = draft ?? base;
  const byId = new Map(models.map(model => [model.id, model]));
  const main = byId.get(current.model);
  const attachment = current.attachment_model ? byId.get(current.attachment_model) : undefined;
  const providerOf = (model: OpenRouterModel) => model.provider ?? 'openrouter';
  const provider = main ? providerOf(main) : 'openrouter';
  const direct = provider === 'deepinfra' || provider === 'infercom';
  const attachmentRequired = !!main && !direct && !readsBoth(main);
  const attachmentModels = models.filter(readsBoth);
  const fallbackModels = models.filter(
    model => model.id !== current.model && providerOf(model) === 'openrouter'
  );
  const changed = !!draft && !sameChoice(draft, base);
  const valid =
    !!main &&
    (current.attachment_model === null ? !attachmentRequired : readsBoth(attachment)) &&
    (current.fallback_model === null ||
      (byId.has(current.fallback_model) && current.fallback_model !== current.model));

  const update = (next: Partial<ChatModelChoice>) => {
    const merged = { ...current, ...next, revision: draft?.revision ?? settings.revision };
    // The fallback must differ from the main model.
    if (merged.fallback_model === merged.model) merged.fallback_model = null;
    setDraft(merged);
    setError(null);
  };
  const save = async () => {
    if (!changed || !valid || mutation.isPending) return;
    setError(null);
    try {
      await mutation.mutateAsync({
        model: current.model,
        attachment_model: current.attachment_model,
        fallback_model: current.fallback_model,
        revision: draft?.revision ?? settings.revision,
      });
      setDraft(null);
      toast.success('Patient chat model saved.');
    } catch (error) {
      if (isChatModelConflict(error)) {
        setDraft(null);
        setError(CONFLICT);
      } else {
        setError(chatModelError(error));
      }
    }
  };

  return (
    <form
      aria-label="Patient chat model"
      className="max-w-2xl space-y-4 rounded-3xl border p-4"
      onSubmit={event => {
        event.preventDefault();
        void save();
      }}
    >
      <label className="block space-y-1 text-sm">
        <span>Provider</span>
        <select
          aria-label="Patient chat provider"
          value={provider}
          disabled={mutation.isPending}
          className="h-9 w-full rounded-3xl border border-input bg-background px-3"
          onChange={event => {
            const next = models.find(item => providerOf(item) === event.target.value);
            if (next) update({ model: next.id, fallback_model: null });
          }}
        >
          {Object.entries({
            openrouter: 'OpenRouter',
            deepinfra: 'DeepInfra',
            infercom: 'Infercom',
          }).map(([id, name]) => {
            const available = models.some(item => providerOf(item) === id);
            return (
              <option key={id} value={id} disabled={!available}>
                {name}
                {!available ? ' · unavailable' : ''}
              </option>
            );
          })}
          <option value="codex" disabled>
            Codex CLI · no Patient Chat runner
          </option>
        </select>
      </label>
      <ModelCombobox
        label="Model"
        value={current.model}
        models={models.filter(item => providerOf(item) === provider)}
        byId={byId}
        required
        disabled={mutation.isPending}
        onChange={id => id && update({ model: id })}
        hint={
          attachmentRequired ? (
            <p role="note" className="flex items-start gap-1 text-foreground">
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {TEXT_ONLY}
            </p>
          ) : (
            'The model that answers patients.'
          )
        }
      />
      <ModelCombobox
        label="Attachment model"
        value={current.attachment_model}
        models={attachmentModels}
        byId={byId}
        required={attachmentRequired}
        clearLabel={attachmentRequired ? undefined : 'No attachment model'}
        disabled={mutation.isPending}
        notice={attachmentRequired ? TEXT_ONLY : undefined}
        onChange={id => update({ attachment_model: id })}
        hint={
          attachmentRequired
            ? 'Answers turns with images or PDFs. Only models that read both are listed.'
            : direct
              ? 'With no attachment model, images and PDFs use Chat’s existing Anthropic route.'
              : 'Unused while the main model reads images and PDFs. Only models that read both are listed.'
        }
      />
      <ModelCombobox
        label="Fallback model"
        value={current.fallback_model}
        models={direct ? [] : fallbackModels}
        byId={byId}
        clearLabel="No fallback model"
        disabled={mutation.isPending || direct}
        onChange={id => update({ fallback_model: id })}
        hint={
          direct
            ? 'Direct providers have no model fallback.'
            : 'OpenRouter switches to this model when the main model is down, rate-limited or refuses a request.'
        }
      />
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={mutation.isPending || !changed || !valid}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {draft && (
          <Button
            type="button"
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => {
              setDraft(null);
              setError(null);
            }}
          >
            Discard
          </Button>
        )}
      </div>
    </form>
  );
}

export function ChatModelPage() {
  const settings = useChatModel();
  const list = useChatModelList(!!settings.data);
  return (
    <div className="space-y-4">
      <PageHeader
        title="Patient chat model"
        description="The model behind the patient chat (Ivelion) in this environment. Chat stores it, so each environment has its own."
      />
      <p className="text-sm text-muted-foreground">
        This page does not change Rudi. Rudi's chat models are on the{' '}
        <Link to="/models" className="underline">
          Models
        </Link>{' '}
        page.
      </p>
      {settings.error && (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>{chatModelError(settings.error)}</p>
          <Button variant="outline" onClick={() => void settings.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {settings.data ? (
        <>
          {!settings.data.openrouter_configured && !settings.data.configured_providers?.length && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-3xl border border-destructive/50 p-3 text-sm"
            >
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              Chat has no configured language model provider in this environment; it still answers
              with Anthropic.
            </p>
          )}
          <Summary settings={settings.data} />
          {list.data ? (
            <ChatModelForm settings={settings.data} models={list.data.models} />
          ) : list.isLoading ? (
            <p>Loading models…</p>
          ) : list.error ? (
            <div role="alert" className="space-y-2 text-sm text-destructive">
              <p>Could not load the model list. {chatModelError(list.error)}</p>
              <Button variant="outline" onClick={() => void list.refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              This Chat release does not list models yet.
            </p>
          )}
        </>
      ) : settings.isLoading ? (
        <p>Loading the patient chat model…</p>
      ) : settings.error ? null : (
        <section className="space-y-2 rounded-3xl border p-4">
          <h2 className="text-sm font-medium">Patient chat model unavailable</h2>
          <p className="text-sm text-muted-foreground">
            This Chat release has no model settings yet, or this Cortex has no connection to Chat.
            The setting is only on the main Rudi connection.
          </p>
          <Button variant="outline" onClick={() => void settings.refetch()}>
            Check again
          </Button>
        </section>
      )}
    </div>
  );
}
